<template>
  <div class="erp-page page-wrap">
    <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>
    <v-card class="erp-filter-card mb-4">
      <div class="erp-page-header mb-3">
        <h1 class="erp-page-title">Períodos Contables</h1>
        <HelpHint text="Definen qué fechas están abiertas para registrar asientos. Si hay partidas pendientes o asientos no publicados, no se podrá cerrar." />
      </div>

      <v-row dense>
        <v-col cols="12" md="3">
          <v-text-field v-model="form.name" dense outlined label="Nombre" />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field v-model="form.dateFrom" dense outlined type="date" label="Desde" />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field v-model="form.dateTo" dense outlined type="date" label="Hasta" />
        </v-col>
        <v-col cols="12" md="3" class="d-flex align-center">
          <v-btn color="primary" @click="createPeriod">Crear período</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="pa-4 mb-4">
      <div class="d-flex align-center mb-2"><v-icon color="primary" class="mr-2">mdi-bank-check</v-icon><h2 class="subtitle-1 font-weight-bold">Cuentas de tesorería y contabilidad</h2></div>
      <p class="body-2 grey--text text--darken-1">Elegí la cuenta contable que corresponde a cada caja, banco o cartera de cheques. Se utilizará para los nuevos cobros; los asientos anteriores no cambian.</p>
      <v-alert v-if="mappingSuccess" type="success" dense outlined>{{ mappingSuccess }}</v-alert>
      <v-expansion-panels flat class="mb-3"><v-expansion-panel><v-expansion-panel-header>Crear cuenta contable para tesorería</v-expansion-panel-header><v-expansion-panel-content>
        <p class="caption">Usá el código y nombre definidos en tu plan de cuentas. Consultá con contabilidad si no los conocés.</p>
        <v-row dense><v-col cols="12" md="3"><v-text-field v-model.trim="newAccount.code" label="Código" outlined dense /></v-col><v-col cols="12" md="4"><v-text-field v-model.trim="newAccount.name" label="Nombre" outlined dense /></v-col><v-col cols="12" md="3"><v-select v-model="newAccount.accountType" :items="treasuryAccountTypes" label="Tipo" outlined dense /></v-col><v-col cols="12" md="2"><v-btn color="primary" :loading="creatingAccount" :disabled="!newAccount.code || !newAccount.name || !newAccount.accountType" @click="createTreasuryAccount">Crear</v-btn></v-col></v-row>
      </v-expansion-panel-content></v-expansion-panel></v-expansion-panels>
      <v-row v-for="mapping in mappings" :key="mapping.treasuryAccountId" align="center" dense>
        <v-col cols="12" md="4"><strong>{{ mapping.treasuryName }}</strong><div class="caption grey--text">{{ mapping.treasuryCode }} · {{ mapping.treasuryType }}</div></v-col>
        <v-col cols="12" md="6"><v-autocomplete v-model="mapping.selectedAccountId" :items="accountChoices(mapping.treasuryType)" item-text="label" item-value="id" label="Cuenta contable" outlined dense hide-details :disabled="!mapping.treasuryActive" /></v-col>
        <v-col cols="12" md="2"><v-btn color="primary" outlined :loading="savingMapping === mapping.treasuryAccountId" :disabled="!mapping.selectedAccountId || mapping.selectedAccountId === mapping.accountingAccountId || !mapping.treasuryActive" @click="saveMapping(mapping)">Guardar</v-btn></v-col>
      </v-row>
    </v-card>
    <v-card>
      <v-data-table :headers="headers" :items="rows" :loading="loading" dense>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="item.status === 'open' ? 'success' : 'grey'" dark>{{ item.status }}</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn x-small color="warning" v-if="item.status === 'open'" @click="closePeriod(item)">Cerrar</v-btn>
          <v-btn x-small color="success" v-else @click="reopenPeriod(item)">Reabrir</v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { accountingService } from "@/services/accounting";
import HelpHint from "@/components/HelpHint.vue";

export default {
  name: "AccountingPeriodsView",
  components: { HelpHint },
  data() {
    return {
      loading: false,
      error: "",
      rows: [],
      mappings: [], accountingAccounts: [], savingMapping: null, mappingSuccess: "",
      creatingAccount: false, newAccount: { code: "", name: "", accountType: "" },
      treasuryAccountTypes: [{ text: "Caja", value: "asset_cash" }, { text: "Banco", value: "asset_bank" }, { text: "Cheques", value: "asset_check" }, { text: "Tarjetas", value: "asset_card" }],
      form: { name: "", dateFrom: "", dateTo: "" },
      headers: [
        { text: "Nombre", value: "name" },
        { text: "Desde", value: "dateFrom" },
        { text: "Hasta", value: "dateTo" },
        { text: "Estado", value: "status" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
    };
  },
  mounted() {
    this.fetchRows();
    this.loadMappings();
  },
  methods: {
    accountChoices(type) { return this.accountingAccounts.filter(a => a.accountType === `asset_${type}`).map(a => ({ id: a.id, label: `${a.code} - ${a.name}` })); },
    async createTreasuryAccount() {
      this.creatingAccount = true; this.error = ""; this.mappingSuccess = "";
      try { await accountingService.createTreasuryPostingAccount(this.newAccount); this.mappingSuccess = "Cuenta contable creada. Ahora podés asignarla a una cuenta de tesorería."; this.newAccount = { code: "", name: "", accountType: "" }; await this.loadMappings(); }
      catch (err) { this.error = this.parseError(err); }
      finally { this.creatingAccount = false; }
    },
    async loadMappings() {
      try {
        const response = await accountingService.getTreasuryPostingMap();
        const data = response.data?.data || {};
        this.mappings = (data.mappings || []).map(row => ({ ...row, selectedAccountId: row.accountingAccountId }));
        this.accountingAccounts = data.accounts || [];
      } catch (err) { this.error = this.parseError(err); }
    },
    async saveMapping(mapping) {
      this.savingMapping = mapping.treasuryAccountId; this.error = ""; this.mappingSuccess = "";
      try {
        await accountingService.saveTreasuryPostingMap(mapping.treasuryAccountId, mapping.selectedAccountId);
        this.mappingSuccess = `Configuración guardada para ${mapping.treasuryName}.`;
        await this.loadMappings();
      } catch (err) { this.error = this.parseError(err); }
      finally { this.savingMapping = null; }
    },
    parseError(err) {
      return err?.response?.data?.error || err?.response?.data?.message || err?.message || "Error inesperado";
    },
    async fetchRows() {
      this.loading = true;
      this.error = "";
      try {
        const { data } = await accountingService.listPeriods();
        this.rows = Array.isArray(data?.data) ? data.data : [];
      } catch (err) {
        this.error = this.parseError(err);
      } finally {
        this.loading = false;
      }
    },
    async createPeriod() {
      if (!this.form.name || !this.form.dateFrom || !this.form.dateTo) return;
      this.error = "";
      try {
        await accountingService.createPeriod(this.form);
        this.form = { name: "", dateFrom: "", dateTo: "" };
        await this.fetchRows();
      } catch (err) {
        this.error = this.parseError(err);
      }
    },
    async closePeriod(item) {
      this.error = "";
      try {
        await accountingService.closePeriod(item.id);
        await this.fetchRows();
      } catch (err) {
        this.error = this.parseError(err);
      }
    },
    async reopenPeriod(item) {
      this.error = "";
      try {
        await accountingService.reopenPeriod(item.id);
        await this.fetchRows();
      } catch (err) {
        this.error = this.parseError(err);
      }
    },
  },
};
</script>

<style scoped>
.page-wrap { display: flex; flex-direction: column; gap: 12px; }
</style>
